import React from 'react';
import { funnelStages } from '../../mock';
import { FileText, Video, Radio, Users, ClipboardCheck, Trophy } from 'lucide-react';

/**
 * Compact micro-thumbnail rendered per funnel stage — visualizes the type
 * of asset served at each stage without relying on stock imagery.
 */
function StageThumb({ variant }) {
  if (variant === 'tofu') {
    return (
      <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-[#4ECDC4]/20 via-[#12234C] to-[#0F1C40] border border-white/10">
        {/* faux blog card + ad banner */}
        <div className="absolute inset-0 p-2 flex gap-1.5">
          <div className="w-1/2 bg-[#0A1530] border border-white/10 rounded-md p-1.5">
            <div className="h-2/3 bg-gradient-to-br from-[#4ECDC4]/40 to-transparent rounded-sm" />
            <div className="mt-1 h-1 rounded bg-white/30 w-3/4" />
            <div className="mt-0.5 h-1 rounded bg-white/15 w-1/2" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="bg-[#0A1530] border border-white/10 rounded-md p-1.5">
              <div className="flex items-center gap-1 mb-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FFB020]" />
                <div className="h-1 bg-white/25 rounded flex-1" />
              </div>
              <div className="h-1 bg-white/15 rounded w-4/5" />
            </div>
            <div className="bg-[#0A1530] border border-white/10 rounded-md p-1.5">
              <div className="h-1 bg-[#4ECDC4]/70 rounded w-2/3 mb-0.5" />
              <div className="h-1 bg-white/15 rounded w-full" />
            </div>
          </div>
        </div>
        <div className="absolute top-1 right-1 text-[7px] font-mono uppercase text-[#4ECDC4] bg-black/40 px-1 rounded">
          Awareness
        </div>
      </div>
    );
  }
  if (variant === 'mofu') {
    return (
      <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-[#FFB020]/15 via-[#12234C] to-[#0F1C40] border border-white/10">
        {/* whitepaper cover + webinar tile */}
        <div className="absolute inset-0 p-2 flex gap-1.5">
          <div className="w-2/5 bg-[#F1EDE4] rounded-md p-1.5 flex flex-col justify-between">
            <div>
              <div className="text-[6px] font-mono uppercase text-[#0F1C40]/60 tracking-widest">Whitepaper</div>
              <div className="h-1 bg-[#0F1C40] rounded w-full mt-1" />
              <div className="h-1 bg-[#0F1C40]/70 rounded w-3/4 mt-0.5" />
            </div>
            <div className="h-0.5 bg-[#FFB020] rounded w-1/3" />
          </div>
          <div className="flex-1 bg-[#050A1F] border border-white/10 rounded-md p-1.5 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB020]/30 to-transparent" />
            <div className="relative flex items-center gap-1">
              <Video className="w-2 h-2 text-[#FFB020]" strokeWidth={2} />
              <div className="text-[6px] font-mono text-[#FFB020] uppercase">Live webinar</div>
            </div>
            <div className="relative mt-1 h-1 bg-white/25 rounded w-3/4" />
            <div className="relative mt-0.5 h-1 bg-white/15 rounded w-1/2" />
            <div className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-[#FFB020] flex items-center justify-center">
              <div className="w-0 h-0 border-l-[3px] border-l-[#0F1C40] border-y-[2px] border-y-transparent ml-0.5" />
            </div>
          </div>
        </div>
        <div className="absolute top-1 right-1 text-[7px] font-mono uppercase text-[#FFB020] bg-black/40 px-1 rounded">
          Consideration
        </div>
      </div>
    );
  }
  // bofu
  return (
    <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-[#4ECDC4]/25 via-[#0F1C40] to-[#050A1F] border border-white/10">
      {/* demo request + case study */}
      <div className="absolute inset-0 p-2 flex gap-1.5">
        <div className="w-1/2 bg-[#0A1530] border border-white/10 rounded-md p-1.5">
          <div className="flex items-center gap-1 mb-1">
            <ClipboardCheck className="w-2 h-2 text-[#4ECDC4]" strokeWidth={2} />
            <div className="text-[6px] font-mono uppercase text-[#4ECDC4]">BANT Form</div>
          </div>
          {['Budget', 'Authority', 'Need', 'Time'].map((f) => (
            <div key={f} className="flex items-center gap-1 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-sm bg-[#4ECDC4]" />
              <div className="h-0.5 bg-white/25 rounded flex-1" />
            </div>
          ))}
        </div>
        <div className="flex-1 bg-[#F1EDE4] rounded-md p-1.5 flex flex-col justify-between">
          <div>
            <div className="text-[6px] font-mono uppercase text-[#0F1C40]/60 tracking-widest">Case Study</div>
            <div className="h-1 bg-[#0F1C40] rounded w-full mt-1" />
            <div className="h-1 bg-[#0F1C40]/70 rounded w-2/3 mt-0.5" />
          </div>
          <div className="flex items-center gap-1">
            <Trophy className="w-2 h-2 text-[#FFB020]" strokeWidth={2} />
            <div className="text-[6px] font-mono text-[#0F1C40] uppercase">+2.4× ROI</div>
          </div>
        </div>
      </div>
      <div className="absolute top-1 right-1 text-[7px] font-mono uppercase text-[#4ECDC4] bg-black/50 px-1 rounded">
        Decision
      </div>
    </div>
  );
}

const stageMeta = {
  tofu: {
    variant: 'tofu',
    Icon: FileText,
    assets: ['Content Syndication', 'Display / Programmatic', 'Newsletter Amplification'],
    velocity: '2.4M imp / wk',
  },
  mofu: {
    variant: 'mofu',
    Icon: Radio,
    assets: ['Gated Whitepapers', 'On-demand Webinars', 'Persona Micro-sites'],
    velocity: '18K MQLs / wk',
  },
  bofu: {
    variant: 'bofu',
    Icon: Users,
    assets: ['BANT Qualification', 'Appointment Setting', 'Sales-ready Handoff'],
    velocity: '9.3K SQLs / wk',
  },
};

export default function Funnel() {
  const data = funnelStages; // 3 stages: TOFU / MOFU / BOFU

  return (
    <section className="py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Full-funnel demand generation</span>
            </div>
            <h2 className="text-4xl lg:text-6xl tracking-tight leading-[1.02] font-medium">
              The Demand Gen <span className="font-serif italic font-normal text-[#4ECDC4]">Funnel.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-8 self-end">
            <p className="text-white/70 text-[15px]">
              From awareness to qualified pipeline — engineered end-to-end, with native creative at every stage.
            </p>
            <div className="mt-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Total Delivered</div>
              <div className="text-5xl lg:text-6xl font-serif italic tabular tracking-tight text-[#4ECDC4]">1.8M+</div>
              <div className="text-xs text-white/50">total leads generated</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* SVG funnel */}
          <div className="lg:col-span-7">
            <svg viewBox="0 0 800 480" className="w-full h-auto">
              <defs>
                <linearGradient id="funnel-tofu" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0.55" />
                </linearGradient>
                <linearGradient id="funnel-mofu" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#FFB020" stopOpacity="0.75" />
                </linearGradient>
                <linearGradient id="funnel-bofu" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#FFB020" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#F1EDE4" stopOpacity="0.95" />
                </linearGradient>
              </defs>

              {/* TOFU trapezoid */}
              <polygon points="40,20 760,20 660,150 140,150" fill="url(#funnel-tofu)" stroke="#4ECDC4" strokeOpacity="0.4" />
              {/* MOFU trapezoid */}
              <polygon points="140,160 660,160 560,290 240,290" fill="url(#funnel-mofu)" stroke="#4ECDC4" strokeOpacity="0.4" />
              {/* BOFU trapezoid */}
              <polygon points="240,300 560,300 460,430 340,430" fill="url(#funnel-bofu)" stroke="#FFB020" strokeOpacity="0.4" />

              {/* Labels inside funnel */}
              <text x="400" y="75" textAnchor="middle" fontSize="14" fill="#0F1C40" fontFamily="JetBrains Mono, monospace" letterSpacing="2" fontWeight="600">TOFU · AWARENESS</text>
              <text x="400" y="115" textAnchor="middle" fontSize="44" fill="#0F1C40" fontFamily="Instrument Serif, serif" fontStyle="italic" fontWeight="500">1.2M Leads</text>

              <text x="400" y="210" textAnchor="middle" fontSize="13" fill="#0F1C40" fontFamily="JetBrains Mono, monospace" letterSpacing="2" fontWeight="600">MOFU · ENGAGEMENT</text>
              <text x="400" y="250" textAnchor="middle" fontSize="36" fill="#0F1C40" fontFamily="Instrument Serif, serif" fontStyle="italic" fontWeight="500">316K Leads</text>

              <text x="400" y="345" textAnchor="middle" fontSize="12" fill="#0F1C40" fontFamily="JetBrains Mono, monospace" letterSpacing="2" fontWeight="600">BOFU · QUALIFIED</text>
              <text x="400" y="385" textAnchor="middle" fontSize="30" fill="#0F1C40" fontFamily="Instrument Serif, serif" fontStyle="italic" fontWeight="600">163K Leads</text>

              {/* Conversion labels on right side */}
              <g fontFamily="JetBrains Mono, monospace" fontSize="11" fill="#4ECDC4">
                <text x="780" y="90" textAnchor="end">100%</text>
                <text x="780" y="230" textAnchor="end" fill="#FFB020">26.3%</text>
                <text x="780" y="365" textAnchor="end" fill="#FFB020">13.6%</text>
              </g>
            </svg>
          </div>

          {/* Right side: stage detail cards with thumbnails */}
          <div className="lg:col-span-5 space-y-4">
            {data.map((s, i) => {
              const meta = stageMeta[s.id];
              const Icon = meta.Icon;
              return (
                <div
                  key={s.id}
                  className="bg-[#12234C] border border-white/10 rounded-2xl p-5 hover:border-[#4ECDC4] transition-colors reveal"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 text-[#4ECDC4]" strokeWidth={1.75} />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">{s.tag}</span>
                    </div>
                    <span className="text-[10px] font-mono text-white/40">{s.sub}</span>
                  </div>

                  <div className="grid grid-cols-5 gap-3 items-center">
                    <div className="col-span-2">
                      <StageThumb variant={meta.variant} />
                    </div>
                    <div className="col-span-3">
                      <div className="text-3xl lg:text-4xl font-serif italic tabular tracking-tight text-[#F1EDE4] mb-1">
                        {s.title}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#FFB020]">
                        {meta.velocity}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {meta.assets.map((a) => (
                      <span
                        key={a}
                        className="text-[9px] font-mono uppercase tracking-widest text-white/60 border border-white/10 px-2 py-1 rounded-full"
                      >
                        {a}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${s.width}%`,
                        background:
                          s.id === 'bofu'
                            ? 'linear-gradient(90deg, #4ECDC4, #FFB020)'
                            : '#4ECDC4',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
